import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import Skeleton from '@mui/material/Skeleton';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as sdkTypes from '@vansite/ts-sharetribe-flex-sdk/types';
import Decimal from 'decimal.js';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import config from '../../../config';
import { formatMoney } from '../../../util/currency';
import usePass from '../../../util/hooks/usePass';

const { Money } = sdkTypes;

const closedListingButtonSx = (theme) => ({
  borderLeft: `1px solid ${theme.tokens.matterColorNegative}`,
  width: '100%',
  padding: '15px 24px',
  textAlign: 'center',
});

// Price and CTA of the sticky bar stay together on wide screens instead of drifting to the edges.
const barContentSx = (theme) => ({
  width: '100%',
  maxWidth: { sm: theme.tokens.contentMaxWidth },
  mx: 'auto',
});

const barRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 2,
};

// Long CTA labels (es "Solicitar reserva", "Reserva inmediatamente") wrap on narrow phones
// instead of pushing the button past the right edge.
const ctaButtonSx = { borderRadius: '300px', minWidth: 0, textAlign: 'center' };

const alertSx = {
  bar: {
    width: '100%',
    '.MuiAlert-icon': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  sidebar: { mb: 2 },
};

const sheetSx = {
  zIndex: 6,
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  minHeight: '100vh',
  paddingTop: 4,
  px: 2,
  bgcolor: 'white',
};

// scroll="body" lets the whole dialog scroll, so the guest and date poppers inside the
// paper may grow past its bottom edge instead of being clipped.
const dialogSlotProps = {
  paper: {
    sx: {
      maxWidth: (theme) => theme.tokens.formMaxWidth,
      overflowY: 'visible',
      borderRadius: 2,
      p: 3,
    },
  },
};

const closeButtonSx = {
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 2,
};

// The phone sheet keeps its big centred title and leaves room for the BottomNav;
// the tablet dialog gets a smaller left-aligned title that clears the close button.
const VARIANT_LAYOUT = {
  sheet: {
    rootSx: { width: '100%', pb: '80px' },
    textAlign: 'center',
    titleSx: undefined,
    titleVariant: 'h2',
  },
  dialog: {
    rootSx: { width: '100%' },
    textAlign: 'left',
    titleSx: { pr: 5 },
    titleVariant: 'h4',
  },
};

const lineItemsTotal = (lineItems) => {
  const amount = lineItems.reduce((total, item) => total + item.lineTotal.amount, 0);
  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : config.currency;
  return new Money(new Decimal(amount).round().toNumber(), currency);
};

/**
 * Whether the bar's form container is shown. The tablet dialog is a MUI modal like the checkout;
 * mounted again after a layout switch it would cover an open checkout, so it steps aside while the
 * checkout is open and comes back when it closes. The phone sheet sits below MUI modals anyway.
 * @param {object} params
 * @param {boolean} params.sheetOpen
 * @param {boolean} params.checkoutOpen
 * @param {'sheet' | 'dialog'} params.modalVariant
 * @returns {boolean}
 */
const isFrameOpen = ({ sheetOpen, checkoutOpen, modalVariant }) =>
  sheetOpen && !(modalVariant === 'dialog' && checkoutOpen);

/**
 * Container the booking form opens in: a full-screen sheet sliding up on phones,
 * a centred dialog on tablets.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {() => void} props.onClose
 * @param {boolean} props.open
 * @param {'sheet' | 'dialog'} props.variant
 */
const BookingModalFrame = ({ children, onClose, open, variant }) =>
  variant === 'dialog' ? (
    <Dialog
      fullWidth
      maxWidth={false}
      onClose={onClose}
      open={open}
      scroll="body"
      slotProps={dialogSlotProps}
    >
      {children}
    </Dialog>
  ) : (
    <Portal>
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Box sx={sheetSx}>{children}</Box>
      </Slide>
    </Portal>
  );

/**
 * Title, host line, close button and form of the booking sheet or dialog.
 * @param {object} props
 * @param {string} [props.authorDisplayName]
 * @param {React.ReactNode} props.form
 * @param {'sheet' | 'dialog'} props.modalVariant
 * @param {() => void} props.onClose
 * @param {boolean} props.showBookingDatesForm
 * @param {string} props.title
 */
const BookingSheetContent = ({
  authorDisplayName,
  form,
  modalVariant,
  onClose,
  showBookingDatesForm,
  title,
}) => {
  const intl = useIntl();
  const layout = VARIANT_LAYOUT[modalVariant];

  return (
    <Box sx={layout.rootSx}>
      <IconButton
        aria-label={intl.formatMessage({ id: 'Modal.closeModal' })}
        onClick={onClose}
        sx={closeButtonSx}
        variant="roundMinimal"
      >
        <CloseIcon />
      </IconButton>

      <Typography
        component="h2"
        gutterBottom
        sx={layout.titleSx}
        textAlign={layout.textAlign}
        variant={layout.titleVariant}
      >
        {title}
      </Typography>
      <Typography mb={4} textAlign={layout.textAlign} variant="body2">
        <FormattedMessage id="BookingPanel.hostedBy" values={{ name: authorDisplayName }} />
      </Typography>

      <Box width="100%">{showBookingDatesForm ? form : null}</Box>
    </Box>
  );
};

/**
 * Sticky bar: nightly price, CTA that opens the sheet, and the sheet itself.
 */
const BarContent = ({
  alert,
  authorDisplayName,
  category,
  checkoutOpen = false,
  closedHint,
  form,
  formattedPrice,
  modalVariant = 'sheet',
  onCloseSheet,
  onOpenSheet,
  priceTitle,
  sheetOpen = false,
  showAnnualPassError,
  showBookingDatesForm,
  title,
}) => {
  const [annualPassIsValid] = usePass();

  return (
    <>
      <Stack spacing={1} sx={barContentSx}>
        {alert}
        <Box sx={barRowSx}>
          {!annualPassIsValid ? (
            <Typography component="h4" title={priceTitle} variant="h5">
              {formattedPrice}&nbsp;
              <FormattedMessage id="BookingPanel.perNight" />
            </Typography>
          ) : null}

          {showBookingDatesForm ? (
            <Button
              color="success"
              disabled={!!showAnnualPassError}
              fullWidth={annualPassIsValid}
              onClick={onOpenSheet}
              sx={ctaButtonSx}
              variant="contained"
            >
              <FormattedMessage
                id={
                  category === 'expressBooking'
                    ? 'BookingPanel.expressCtaButtonMessage'
                    : 'BookingPanel.ctaButtonMessage'
                }
              />
            </Button>
          ) : (
            closedHint
          )}
        </Box>
      </Stack>

      <BookingModalFrame
        onClose={onCloseSheet}
        open={isFrameOpen({ sheetOpen, checkoutOpen, modalVariant })}
        variant={modalVariant}
      >
        <BookingSheetContent
          authorDisplayName={authorDisplayName}
          form={form}
          modalVariant={modalVariant}
          onClose={onCloseSheet}
          showBookingDatesForm={showBookingDatesForm}
          title={title}
        />
      </BookingModalFrame>
    </>
  );
};

/**
 * Total of the customer line items, a hint before dates are picked, or a skeleton while loading.
 * @param {object} props
 * @param {boolean} props.fetchLineItemsInProgress
 * @param {boolean} props.hasDatesSelected
 * @param {Array} props.lineItems
 */
const SidebarPriceLine = ({ fetchLineItemsInProgress, hasDatesSelected, lineItems }) => {
  const intl = useIntl();

  if (!hasDatesSelected) {
    return (
      <Typography color="text.secondary" mb={2} variant="body1">
        <FormattedMessage id="BookingPanel.selectDatesToShowPrices" />
      </Typography>
    );
  }
  if (fetchLineItemsInProgress) {
    return <Skeleton height={32} sx={{ mb: 2 }} variant="text" width={180} />;
  }
  if (lineItems.length === 0) return null;

  const totalPrice = lineItemsTotal(lineItems.filter((item) => item.includeFor.includes('customer')));
  const nightCount = lineItems.find((item) => item.code === 'line-item/night')?.quantity || 0;

  return (
    <Box alignItems="baseline" display="flex" gap={1} mb={2}>
      <Typography component="span" variant="h5">
        {formatMoney(intl, totalPrice)}
      </Typography>
      {nightCount > 0 ? (
        <Typography color="text.secondary" component="span" variant="body2">
          <FormattedMessage id="BookingPanel.forNightCount" values={{ count: nightCount }} />
        </Typography>
      ) : null}
    </Box>
  );
};

/**
 * Desktop sidebar: seats, total (or select-dates hint) and the inline form.
 */
const SidebarContent = ({
  alert,
  closedHint,
  currentListing,
  fetchLineItemsInProgress = false,
  form,
  hasDatesSelected = false,
  lineItems = [],
  showBookingDatesForm,
}) => (
  <>
    <Typography color="text.secondary" mb={1} variant="body2">
      <FormattedMessage
        id="ListingPage.availableSeats.text"
        values={{ seats: currentListing.attributes.publicData.amountOfSeats || 1 }}
      />
    </Typography>
    <Box display="flex" flexDirection="column" width={1}>
      <SidebarPriceLine
        fetchLineItemsInProgress={fetchLineItemsInProgress}
        hasDatesSelected={hasDatesSelected}
        lineItems={lineItems}
      />
      {alert}
      {showBookingDatesForm ? form : closedHint}
    </Box>
  </>
);

/**
 * Presentational booking panel. The sticky `bar` (phones and tablets) shows price and CTA and
 * opens the form in a sheet or dialog; the `sidebar` (desktop) shows the seats, the total and the
 * form inline. All state lives in the caller, so a layout switch only swaps the markup. It is not
 * wrapped in `memo`, because the `form` element is created fresh on every render.
 * @param {object} props
 * @param {'bar' | 'sidebar'} props.variant
 * @param {React.ReactNode} props.form - Booking form element, rendered inline or inside the sheet
 * @param {object} props.currentListing
 * @param {string} props.title
 * @param {string} [props.authorDisplayName] - Host name for the "hosted by" line of the sheet
 * @param {string} [props.category]
 * @param {string} [props.formattedPrice] - Nightly price of the bar
 * @param {string} [props.priceTitle]
 * @param {Array} [props.lineItems]
 * @param {boolean} [props.fetchLineItemsInProgress]
 * @param {boolean} [props.hasDatesSelected] - Sidebar: show the total instead of the select-dates hint
 * @param {React.ReactNode} [props.showAnnualPassError] - Message of the annual pass alert, falsy for none
 * @param {boolean} props.showBookingDatesForm
 * @param {boolean} [props.isClosed]
 * @param {boolean} [props.sheetOpen] - Bar: whether the sheet or dialog is open
 * @param {boolean} [props.checkoutOpen] - Bar: the checkout is open, so the tablet dialog steps aside
 * @param {() => void} [props.onOpenSheet]
 * @param {() => void} [props.onCloseSheet]
 * @param {'sheet' | 'dialog'} [props.modalVariant] - Bar: container of the form
 */
const BookingPanelContent = ({ isClosed = false, variant, ...props }) => {
  const { showAnnualPassError, showBookingDatesForm } = props;

  const alert = showAnnualPassError ? (
    <Alert severity="warning" sx={alertSx[variant]}>
      {showAnnualPassError}
    </Alert>
  ) : null;

  const closedHint =
    isClosed && !showBookingDatesForm ? (
      <Box component="div" sx={closedListingButtonSx}>
        <FormattedMessage id="BookingPanel.closedListingButtonText" />
      </Box>
    ) : null;

  const Content = variant === 'bar' ? BarContent : SidebarContent;

  return <Content {...props} alert={alert} closedHint={closedHint} />;
};

export default BookingPanelContent;
